/**
 * Alerts Management Page
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as ResolveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Alert, AlertSummary, AlertStatus, AlertSeverity } from '../../types/alerts';

const AlertsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>([]);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch alert summary
  const { data: summary } = useQuery<AlertSummary>({
    queryKey: ['alertSummary'],
    queryFn: async () => {
      const response = await api.getAlertSummary();
      return response.data;
    },
  });

  // Fetch alerts
  const { data: alerts, isLoading, refetch } = useQuery<Alert[]>({
    queryKey: ['alerts', statusFilter, severityFilter],
    queryFn: async () => {
      const params: any = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      const response = await api.getAlerts(params);
      return response.data;
    },
  });

  // Acknowledge mutation
  const acknowledgeMutation = useMutation({
    mutationFn: (alertIds: string[]) => api.acknowledgeAlerts(alertIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertSummary'] });
      setSelectedAlerts([]);
    },
  });

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: ({ alertIds, notes }: { alertIds: string[]; notes?: string }) =>
      api.resolveAlerts(alertIds, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertSummary'] });
      setSelectedAlerts([]);
      setResolveDialogOpen(false);
      setResolutionNotes('');
    },
  });

  // Evaluate alerts mutation (manual trigger)
  const evaluateMutation = useMutation({
    mutationFn: () => api.evaluateAlerts(),
    onSuccess: (response) => {
      alert(response.data.message);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['alertSummary'] });
    },
  });

  const handleSelectAlert = (alertId: string) => {
    setSelectedAlerts((prev) =>
      prev.includes(alertId) ? prev.filter((id) => id !== alertId) : [...prev, alertId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAlerts.length === alerts?.length) {
      setSelectedAlerts([]);
    } else {
      setSelectedAlerts(alerts?.map((a) => a.alert_id) || []);
    }
  };

  const handleAcknowledge = () => {
    if (selectedAlerts.length > 0) {
      acknowledgeMutation.mutate(selectedAlerts);
    }
  };

  const handleResolve = () => {
    if (selectedAlerts.length > 0) {
      resolveMutation.mutate({ alertIds: selectedAlerts, notes: resolutionNotes });
    }
  };

  const getSeverityColor = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return 'error';
      case AlertSeverity.WARNING:
        return 'warning';
      case AlertSeverity.INFO:
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: AlertStatus) => {
    switch (status) {
      case AlertStatus.ACTIVE:
        return 'error';
      case AlertStatus.ACKNOWLEDGED:
        return 'warning';
      case AlertStatus.RESOLVED:
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Alerts & Notifications</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => evaluateMutation.mutate()}
            disabled={evaluateMutation.isPending}
          >
            Evaluate Alerts
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/alert-rules')}
          >
            Manage Rules
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Alerts
              </Typography>
              <Typography variant="h4">{summary?.total_alerts || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active
              </Typography>
              <Typography variant="h4" color="error.main">
                {summary?.active_alerts || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'var(--color-danger-bg)', color: 'error.contrastText' }}>
            <CardContent>
              <Typography gutterBottom>Critical</Typography>
              <Typography variant="h4">{summary?.critical_count || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'var(--color-warning-bg)' }}>
            <CardContent>
              <Typography gutterBottom>Warning</Typography>
              <Typography variant="h4">{summary?.warning_count || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'var(--color-info-bg)' }}>
            <CardContent>
              <Typography gutterBottom>Info</Typography>
              <Typography variant="h4">{summary?.info_count || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Alert Status</InputLabel>
          <Select
            value={statusFilter}
            label="Alert Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="acknowledged">Acknowledged</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={severityFilter}
            label="Severity"
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="info">Info</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        {selectedAlerts.length > 0 && (
          <>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={handleAcknowledge}
              disabled={acknowledgeMutation.isPending}
            >
              Acknowledge ({selectedAlerts.length})
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<ResolveIcon />}
              onClick={() => setResolveDialogOpen(true)}
              disabled={resolveMutation.isPending}
            >
              Resolve ({selectedAlerts.length})
            </Button>
          </>
        )}
      </Box>

      {/* Alerts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedAlerts.length === alerts?.length && alerts.length > 0}
                  indeterminate={
                    selectedAlerts.length > 0 && selectedAlerts.length < (alerts?.length || 0)
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Alert Status</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Triggered</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : alerts && alerts.length > 0 ? (
              alerts.map((alert) => (
                <TableRow key={alert.alert_id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedAlerts.includes(alert.alert_id)}
                      onChange={() => handleSelectAlert(alert.alert_id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={alert.severity.toUpperCase()}
                      color={getSeverityColor(alert.severity) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={alert.status.toUpperCase()}
                      color={getStatusColor(alert.status) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {alert.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {alert.message.length > 100
                        ? `${alert.message.substring(0, 100)}...`
                        : alert.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(alert.triggered_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No alerts found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Alerts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to resolve {selectedAlerts.length} alert(s). Optionally, add resolution notes:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Resolution Notes"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            placeholder="Describe how the issue was resolved..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleResolve}
            disabled={resolveMutation.isPending}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertsPage;
